<template>
    <div class="grid grid-cols-1 lg:grid-cols-2 px-0 lg:px-4 py-1 lg:py-5 bg-white sm:p-6">
        <!-- First Name -->
        <div class="lg:mr-4 gap-6">
            <floating-input     v-model:value="firstName"
                                id="firstName"
                                label="First Name"
            ></floating-input>
        </div>

        <!-- Last Name -->
        <div class="lg:ml-4 gap-6">
            <floating-input     v-model:value="lastName"
                                id="lastName"
                                label="Last Name"
            ></floating-input>
        </div>

        <!-- Email -->
        <div    class="lg:mr-4 gap-6">
            <floating-input     v-model:value="email"
                                id="email"
                                label="Email"
                                :readonly="true"
            ></floating-input>
        </div>

        <!-- Phone Number -->
        <div    class="lg:ml-4 gap-6">
            <floating-input     v-model:value="phoneNumber"
                                id="phoneNumber"
                                label="Phone Number (XXXXXXXXXX)"
            ></floating-input>
        </div>

        <!-- Password Reset Email -->
        <div    v-if="permissions.can('send_password_resets')"
                class="gap-6 lg:my-2 lg:col-span-2">
            <div    v-if="ssoEnabled">
                <banner     bg-color="bg-blue-400"
                            fg-color="text-white"
                >
                    <template   #banner-container>
                                                <span   class="text-white">
                                                    User has SSO Enabled.
                                                </span>
                    </template>
                </banner>
            </div>
            <button-blue-secondary  v-else
                                    @click="sendResetPasswordEmail"
            >
                                        <span   v-if="!resettingPassword && errorResettingPassword === null"
                                        >
                                            Send Password Reset Email
                                        </span>
                <span   v-else-if="resettingPassword">
                                            Sending Password Reset Email
                                        </span>
                <span   v-else
                        class="text-red-500">
                                            {{ errorResettingPassword }}
                                        </span>
            </button-blue-secondary>
        </div>

      <!-- Change User Email -->
      <div class="gap-6 lg:my-2 lg:col-span-2">
        <button-blue-secondary @click="navigateToUserEmailChangePage">
          <span>
            Change User's Primary Email Address
          </span>
        </button-blue-secondary>
      </div>

      <!-- Company Id -->
        <div    v-show="permissions.can('choose_user_company')"
                class="flex flex-col mt-4 lg:mt-0 lg:col-span-2">
            <span   class="font-medium text-sm text-gray-700">User Company</span>
            <select2
                :close-on-select="true"
                :options="companyList"
                v-model:value="companyId"
            ></select2>
        </div>

      <fieldset v-if="initialAdminTrainingCompletedAt == null" class="mt-4 col-span-2">
        <legend class="sr-only">Training</legend>
        <div class="space-y-5">
          <div class="relative flex items-start">
            <div class="flex h-6 items-center">
              <input id="certify-training" aria-describedby="certify-training"
                     v-model="certify"
                     name="certify-training" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600">
            </div>
            <div class="ml-3 text-sm leading-6">
              <label for="comments" class="font-medium text-gray-900">Certify Training</label>
              <p id="comments-description" class="text-gray-500">Certify {{ user.display_name }} as having completed training today.</p>
            </div>
          </div>
        </div>
      </fieldset>

      <div v-else
           class="rounded-md bg-green-50 p-4 col-span-2 mt-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-green-800">Training Certified on {{ initialAdminTrainingCompletedAt }}
              <span v-if="initialAdminTrainingRecordedByUser.display_name ?? null">
                by {{ initialAdminTrainingRecordedByUser.display_name}}
              </span>
            </p>
          </div>
        </div>
      </div>

    </div>
</template>

<script>
import axios from "axios"
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Select2 from "@/components/elements/select2"
import FloatingInput from "@/components/elements/FloatingInput";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import Banner from "@/components/Banners/Banner";
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

export default {
    name: "UserEditPersonalInformation",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {Select2, FloatingInput, ButtonBlueSecondary, Banner},
    props: {
        user: Object,
        companyList: Object,
        save: {
            type: [Boolean, String],
            required: true,
        },
        groups: {
            type: Array,
            default() {
                return null
            },
        },
        permissionLevel: {
            type: String,
            default: null,
        },
        isEditingSelf: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {

            loading: false,

            // User Info
            firstName: this.user.first_name,
            lastName: this.user.last_name,
            email: this.user.email,
            phoneNumber: this.user.phone_number,
            initialAdminTrainingCompletedAt: this.user.initial_admin_training_completed_at,
            initialAdminTrainingRecordedByUser: this.user.initial_admin_training_recorded_by_user,
            certify: false,
            companyId: null,
            ssoEnabled: this.user.sso_enabled,

            resettingPassword: false,
            errorResettingPassword: null,
            errors: null,
        }
    },
    created() {

    },
    mounted() {
        this.companyId = this.user.company_id
    },
    methods: {
        saveUser() {
            if (!this.canEditUser && !this.isEditingSelf) {
                this.$emit('update:save', 'Unable to Edit User.')
                return
            }

            this.errors = null
            let params = {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phoneNumber: this.phoneNumber,
                companyId: this.companyId,
                certify: this.certify,
            }
            if (!_.isNil(this.permissionLevel)) {
                params.permissionLevel = this.permissionLevel
            }
            if (!_.isNil(this.groups)) {
                params.groups = this.groups
            }
            axios.put(this.company.buildCompanyApiInternalRoute(`users/${this.user.id}`),
                params
            ).then(response => {
                let data = response.data.data
                this.firstName = data.first_name
                this.lastName = data.last_name
                this.email = data.email
                this.phoneNumber = data.phone_number
              console.log(data);
                console.log(data.initial_admin_training_completed_at)
                console.log(data.initial_admin_training_recorded_by_user)
                this.initialAdminTrainingCompletedAt = data.initial_admin_training_completed_at;
                this.initialAdminTrainingRecordedByUser = data.initial_admin_training_recorded_by_user;

                this.$emit('update:save', false)
            }).catch(error => {
                console.log(`Failed to save user: ${error}`)
                this.$emit('update:save', error)
            }).then(() => {

            })
        },
        sendResetPasswordEmail() {
            if (this.resettingPassword || this.user.sso_enabled) {
                return
            }

            this.resettingPassword = true
            axios.post('/password/email', {
                email: this.email,
            }).then(response => {
                console.log('Password Reset Email Sent')
            }).catch(error => {
                this.errorResettingPassword = 'An Error Occurred sending the Password Reset Email.'
                console.log('Failed to Send Password Reset Email')
                console.log(error)
            }).then(() => {
                this.resettingPassword = false
            })
        },
        navigateToUserEmailChangePage(){
            window.location.href = this.company.buildCompanyRoute(`users/${this.user.id}/email`)
        },
    },
    computed: {
        canEditUser() {
            return this.permissions.can('update_users')
        },
    },
    watch: {
        save(val, old) {
            if (val !== old && val === true) {
                console.log('Save User Personal Info')
                this.saveUser()
            }
        }
    },
}
</script>

<style scoped>

</style>